import { adminConstants } from "actions/types";
import { find, keyBy, map, transform } from "lodash";
import type { AdminPaginated, AdminState } from "types/state";

interface AdminAction {
  type: string;
  data?: unknown;
}

const initialState: AdminState = {
  approvalsById: {},
  complianceReports: {},
  usersById: {},
  productFeatures: [],
  companies: [],
  companiesCount: 0,
  payrolls: {
    results: [],
    totalRows: 0,
  },
  missedPayrolls: {
    results: [],
    totalRows: 0,
  },
  workflows: {
    results: [],
    totalRows: 0,
  },
  workflowSteps: {
    results: [],
    totalRows: 0,
  },
  foundUsers: [],
  foundUsersCount: 0,
  customReports: {
    results: [],
    totalRows: 0,
  },
  employerOrders: {
    results: [],
    totalRows: 0,
  },
  customReportTypes: {},
};

export function admin(
  state: AdminState = initialState,
  action: AdminAction
): AdminState {
  switch (action.type) {
    case adminConstants.GET_APPROVAL_SUCCESS: {
      const data = action.data as { id: string };
      return {
        ...state,
        approvalsById: {
          ...state.approvalsById,
          [data.id]: data,
        },
      };
    }

    case adminConstants.SET_APPROVAL_STATUS_SUCCESS: {
      const data = action.data as { id: string };
      return {
        ...state,
        approvalsById: {
          ...state.approvalsById,
          [data.id]: {
            ...(state.approvalsById[data.id] as object),
            ...data,
          },
        },
      };
    }

    case adminConstants.GET_COMPLIANCE_REPORTS_SUCCESS: {
      const complianceReports = transform(
        action.data as Array<{ reportType: string; reportHistory: unknown }>,
        (acc: Record<string, unknown>, val) => {
          acc[val.reportType] = val.reportHistory;
          return acc;
        },
        {} as Record<string, unknown>
      );
      return {
        ...state,
        complianceReports,
      };
    }

    case adminConstants.GET_USER_ACCOUNT_REVIEW_DOCUMENTS_SUCCESS: {
      const data = action.data as { id: string };
      const userById = {
        [data.id]: data,
      };
      return {
        ...state,
        usersById: {
          ...state.usersById,
          ...userById,
        },
      };
    }

    case adminConstants.GET_PENDING_APPROVALS_SUCCESS: {
      const approvalsById = (action.data as Array<{ id: string }>).reduce(
        (accum: Record<string, unknown>, approval) => {
          return {
            ...accum,
            [approval.id]: approval,
          };
        },
        {}
      );
      return {
        ...state,
        approvalsById: {
          ...state.approvalsById,
          ...approvalsById,
        },
      };
    }

    case adminConstants.GET_PRODUCT_FEATURES_SUCCESS: {
      return {
        ...state,
        productFeatures: keyBy(action.data as unknown[], "name"),
      };
    }

    case adminConstants.GET_PAGINATED_COMPANIES_SUCCESS: {
      const data = action.data as { rows: unknown[]; count: number };
      return {
        ...state,
        companies: data.rows,
        companiesCount: data.count,
      };
    }

    case adminConstants.GET_PAYROLLS_SUCCESS: {
      return {
        ...state,
        payrolls: {
          ...(action.data as AdminPaginated),
        },
      };
    }

    case adminConstants.GET_UNPROCESSED_PAYROLLS_SUCCESS: {
      return {
        ...state,
        missedPayrolls: {
          ...(action.data as AdminPaginated),
        },
      };
    }

    case adminConstants.SEARCH_USERS_SUCCESS: {
      const data = action.data as { results: unknown[]; totalRows: number };
      return {
        ...state,
        foundUsers: data.results,
        foundUsersCount: data.totalRows,
      };
    }

    case adminConstants.GET_WORKFLOWS_SUCCESS: {
      return {
        ...state,
        workflows: {
          ...(action.data as AdminPaginated),
        },
      };
    }

    case adminConstants.GET_WORKFLOW_STEPS_SUCCESS: {
      return {
        ...state,
        workflowSteps: {
          ...(action.data as AdminPaginated),
        },
      };
    }

    case adminConstants.GET_CUSTOM_REPORT_TYPES_SUCCESS: {
      const customReportsByType = keyBy(action.data as unknown[], "reportType");
      return {
        ...state,
        customReportTypes: customReportsByType,
      };
    }

    case adminConstants.GET_CUSTOM_REPORTS_SUCCESS: {
      const data = action.data as { count: number; reports: unknown[] };
      return {
        ...state,
        customReports: {
          totalRows: data.count,
          results: data.reports,
        },
      };
    }
    case adminConstants.GET_REPORTS_STATUS_SUCCESS: {
      const reports = action.data as Array<{ id: unknown }>;
      const updatedResults = map(state.customReports.results, (result) => {
        const matchingReport = find(reports, {
          id: (result as { id: unknown }).id,
        });

        if (!matchingReport) {
          return result;
        }

        return matchingReport;
      });
      return {
        ...state,
        customReports: {
          ...state.customReports,
          results: updatedResults,
        },
      };
    }

    case adminConstants.GET_EMPLOYER_ORDERS_SUCCESS: {
      const data = action.data as { count: number; rows: unknown[] };
      return {
        ...state,
        employerOrders: {
          totalRows: data.count,
          results: data.rows,
        },
      };
    }

    case adminConstants.GET_PAYCHEX_USERS_REPORT_SUCCESS: {
      return {
        ...state,
        paychexUsersReport: action.data,
      };
    }

    default:
      return state;
  }
}
